// overlayHighlight.js
export function createOverlayHighlighter(deps) {
    const {
      app,
      overlaysM,
      view,
  
      // ✅ add these
      BASE_W,
      BASE_H,
  
      MIN_SCALE,
      MAX_SCALE,
      BASE_SCALE,
      VIEW_ANIM_MS,
      easeFn,
      applyTransform,
      scheduleTilesRedraw,
      animateViewTo,
      crossfadeToSelection,
  
      // optional
      FIT_PADDING_PX = 40,
    } = deps;
  
    let active = null;
    let isAnimating = false;
  
    function getInitialTargetFit() {
      const vw = app.screen.width, vh = app.screen.height;
      const scale = Math.max(
        MIN_SCALE,
        Math.min(MAX_SCALE, Math.min(vw / BASE_W, vh / BASE_H))
      ) * BASE_SCALE;
      return {
        scale,
        tx: (vw - BASE_W * scale) / 2,
        ty: (vh - BASE_H * scale) / 2,
      };
    }
  
    async function animateToInitialState() {
      const target = getInitialTargetFit();
      await animateViewTo(
        view,
        target,
        VIEW_ANIM_MS,
        easeFn,
        applyTransform,
        () => scheduleTilesRedraw()
      );
    }
  
    function viewFitOverlayToScreen(bounds, zoomMul = 1) {
      const vw = app.screen.width, vh = app.screen.height;
      const pad = FIT_PADDING_PX;
  
      const availW = Math.max(1, vw - 2 * pad);
      const availH = Math.max(1, vh - 2 * pad);
  
      const w = Math.max(1e-6, bounds.maxx - bounds.minx);
      const h = Math.max(1e-6, bounds.maxy - bounds.miny);
  
      let scale = Math.min(availW / w, availH / h) * (zoomMul ?? 1);
      scale = Math.max(MIN_SCALE, Math.min(MAX_SCALE, scale));
  
      const cx = (bounds.minx + bounds.maxx) / 2;
      const cy = (bounds.miny + bounds.maxy) / 2;
  
      return { scale, tx: (vw / 2) - cx * scale, ty: (vh / 2) - cy * scale };
    }
  
    async function highlightOverlay({ overlayId, polyIndex, zoomMul = 1 }) {
      if (isAnimating) return false;
  
      const ov = overlaysM.getOverlayById(overlayId);
      if (!ov || !ov.polys?.length) return false;
  
      isAnimating = true;
      active = { overlayId, polyIndex };
  
      const bounds = overlaysM.overlayBounds(ov);
      const target = viewFitOverlayToScreen(bounds, zoomMul);
  
      await Promise.all([
        animateViewTo(view, target, VIEW_ANIM_MS, easeFn, applyTransform, () => scheduleTilesRedraw()),
        crossfadeToSelection(active, 1),
      ]);
  
      isAnimating = false;
      scheduleTilesRedraw();
      return true;
    }
  
    async function handleTapAtBasePoint(bx, by) {
      if (isAnimating) return { action: "ignored" };
  
      const picked = overlaysM.pickAtBasePoint(bx, by);
  
      // ✅ no hit -> animate to initial
      if (!picked) {
        isAnimating = true;
        active = null;
        await Promise.all([animateToInitialState(), crossfadeToSelection(null, 0)]);
        isAnimating = false;
        scheduleTilesRedraw();
        return { action: "reset" };
      }
  
      // ✅ same overlay -> reset too
      if (active && active.overlayId === picked.overlayId) {
        isAnimating = true;
        active = null;
        await Promise.all([animateToInitialState(), crossfadeToSelection(null, 0)]);
        isAnimating = false;
        scheduleTilesRedraw();
        return { action: "reset" };
      }
  
      const ov = overlaysM.getOverlayById(picked.overlayId);
      const zoomMul = ov?.target?.zoom ?? ov?.zoom ?? 1;
  
      await highlightOverlay({ overlayId: picked.overlayId, polyIndex: picked.polyIndex, zoomMul });
      return { action: "highlighted", picked };
    }
  
    return { handleTapAtBasePoint, highlightOverlay };
  }
  